import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { USER_ROLE, useAuth } from '../../../hooks';
import { useCompany } from '../../../hooks/useCompany';
import { useLazyFetchPricingByCompanyIdQuery } from '../../../services/charger.api';
import { useLazyFetchNewLocationsByCompanyIdQuery } from '../../../services/location.api';
import { Price } from '../../../stores/types/price.interface';
import {
  Button,
  ButtonSize,
  ButtonType,
  CheckBoxData,
  ColorType,
  Dropdown,
  GroupDirection,
  Icon,
  Label,
  LabelType,
  RadioGroup,
} from '../../_ui';

interface SelectDropdown {
  id?: string;
  label: string;
  selected: boolean;
}

export const ChargerCompany = (props: any) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { nextStep, wizardState, setWizardState, chargerInfo, companyInfo } = props;

  const { companies: companyList } = useCompany();
  const [triggerFetchLocationByCompanyIds, { data }] = useLazyFetchNewLocationsByCompanyIdQuery();

  const [triggerFetchPricingByCompanyIdQuery, { data: priceModels }] =
    useLazyFetchPricingByCompanyIdQuery();
  const [companyDropdown, setCompanyDropdown] = useState<SelectDropdown[]>([]);
  const [locationDropdown, setLocationDropdown] = useState<SelectDropdown[]>([]);

  const [_moveLicenseOptions, setMoveLicenseOptions] = useState<string>();

  const moveLicenseOptions = () => {
    return [
      {
        id: '1',
        label: t('keep_license_msg'),
        secondaryLabel: t('keep_license_msg_detail'),
        selected: _moveLicenseOptions === '1',
      },
      {
        id: '2',
        label: t('remove_license_msg'),
        secondaryLabel: t('remove_license_msg_detail'),
        selected: _moveLicenseOptions === '2',
      },
    ];
  };

  const handleMoveLicenseOptions = (items: CheckBoxData[]) => {
    const selectedOptions = items.find((item) => item.selected === true);
    const option = selectedOptions?.id as string;
    setMoveLicenseOptions(option);
    setWizardState({
      ...wizardState,
      selMoveLicenseOption: option,
    });
  };

  const renderDivForMoveLicense = () => {
    return (
      <div className='flex flex-col gap-6'>
        <div className='border-y border-grey2' />
        <div className='flex flex-col'>
          <Label
            text={t('charger_assigned_license_msg')}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
          />
          <RadioGroup
            direction={GroupDirection.Vertical}
            defaultItems={moveLicenseOptions()}
            onChange={handleMoveLicenseOptions}
            secondaryLabelType={LabelType.BODY4}
            secondaryLabelColor={ColorType.GREY6}
            padding='mt-4'
            dataTestId='move-license-option'
          />
        </div>
      </div>
    );
  };

  useEffect(() => {
    setWizardState((prev: any) => ({
      ...prev,
      selLocation: null,
    }));
  }, [data, setWizardState]);

  useEffect(() => {
    if (priceModels && priceModels.length > 0) {
      const dropdownOptions = priceModels.map((priceModel: Price) => ({
        id: priceModel.id,
        label: priceModel.displayName,
        selected: priceModels.length === 1,
      }));
      setWizardState((prev: any) => {
        if (priceModels.length === 1) {
          return {
            ...prev,
            pricingModels: dropdownOptions,
            selPriceModel: {
              id: priceModels[0].id,
              label: priceModels[0].displayName,
              selected: true,
            },
          };
        }
        return {
          ...prev,
          pricingModels: dropdownOptions,
        };
      });
    }
  }, [priceModels, setWizardState]);

  useEffect(() => {
    if (companyList && companyList.length > 0) {
      setCompanyDropdown(
        companyList.map((companyObj) => ({
          id: companyObj.id,
          label: companyObj.name,
          selected: wizardState.selCompany?.id === companyObj.id,
        })),
      );
    }
  }, [companyList, wizardState.selCompany?.id]);

  useEffect(() => {
    setLocationDropdown([]);
    if (data && data.entities && data.entities.length > 0) {
      setLocationDropdown(
        data.entities.map((locationObj) => ({
          id: locationObj.id,
          label: locationObj.name,
          selected: wizardState.selLocation?.id === locationObj.id,
        })),
      );
    }
  }, [data, wizardState.selCompany, wizardState.selLocation?.id]);

  useEffect(() => {
    if (locationDropdown.length === 1 && !wizardState.selLocation) {
      setWizardState((prev: any) => {
        if (prev.selLocation) {
          return prev;
        }
        return {
          ...prev,
          selLocation: locationDropdown[0],
        };
      });
      setLocationDropdown(
        locationDropdown.map((locationObj) => ({
          ...locationObj,
          selected: true,
        })),
      );
    }
  }, [locationDropdown, setWizardState, wizardState.selLocation]);

  useEffect(() => {
    setWizardState({
      chargerInfo,
      companyInfo,
      pricingModels: [],
      selLocation: null,
      selCompany: null,
      selPriceModel: null,
      selMoveLicenseOption: null,
      chargerId: chargerInfo.id,
      hidden: chargerInfo.hiddenOnMap,
      directions: chargerInfo?.directions || '',
      note: chargerInfo?.internalNote || '',
      installer: chargerInfo.deploymentInfo?.installationPerson || '',
      installationDate: chargerInfo.deploymentInfo?.installationDate || null,
      installerContact: chargerInfo.deploymentInfo?.installerContactEmail || '',
      deployed: chargerInfo.deploymentInfo?.deployed,
      deploymentDate: chargerInfo.deploymentInfo?.deployedDate || null,
    });
  }, [chargerInfo, companyInfo, setWizardState]);

  const availableCompanies = useMemo(
    () => companyDropdown.filter((item) => item.id !== companyInfo?.id),
    [companyDropdown, companyInfo?.id],
  );

  const availableLocations = useMemo(
    () => locationDropdown.filter((item) => item.id !== chargerInfo.location.id),
    [locationDropdown, chargerInfo.location.id],
  );

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-2'>
        <Label
          text={t('chargers_move_step_1_description_1')}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
        />
        <Label
          text={t('chargers_move_step_1_description_2')}
          type={LabelType.BODY3}
          color={ColorType.BLACK}
        />
      </div>
      <div className='flex flex-row gap-4'>
        <div className='flex flex-col gap-2 w-[280px] justify-between pb-2'>
          <Label
            text={t('charger_current_company')}
            type={LabelType.LABEL_M}
            color={ColorType.BLACK}
          />
          <div className='flex flex-row justify-between'>
            <Label text={companyInfo?.name} type={LabelType.BODY3} color={ColorType.BLACK} />
            <Icon src={ArrowRight} size={IconSize.SIZE_20x20} />
          </div>
        </div>
        <div className='flex flex-col gap-2 w-[220px] justify-between'>
          <Label text={t('charger_new_company')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
          <Dropdown
            headerWidth='220px'
            items={availableCompanies}
            onItemClick={(selectedItem: any) => {
              setLocationDropdown([]);
              triggerFetchLocationByCompanyIds(selectedItem);
              triggerFetchPricingByCompanyIdQuery(selectedItem.id);
              setWizardState({
                ...wizardState,
                selLocation: null,
                selCompany: selectedItem,
              });
            }}
            placeholder={t('charger_choose_new_company')}
            placeholderLabelType={LabelType.BODY3_G4}
            labelType={LabelType.BODY3}
            labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
            contentDivWidth={220}
            contentDivHeight={150}
            chevdownIconHighlightColor={ColorType.GREY6}
            headerHighLightClassName='bg-grey1 border-grey3 rounded h-[40px] mr-1.5'
          />
        </div>
      </div>
      <div className='flex flex-row gap-4'>
        <div className='flex flex-col w-[280px] justify-between pb-2'>
          <Label
            text={t('charger_current_location')}
            type={LabelType.LABEL_M}
            color={ColorType.BLACK}
          />
          <div className='flex flex-row justify-between'>
            <Label
              text={chargerInfo.location.name}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
            <Icon src={ArrowRight} size={IconSize.SIZE_20x20} />
          </div>
        </div>
        <div className='flex flex-col w-[220px] justify-between'>
          <Label
            text={t('charger_new_location')}
            type={LabelType.LABEL_M}
            color={ColorType.BLACK}
            className='mb-2'
          />
          <Dropdown
            headerWidth='220px'
            items={availableLocations}
            onItemClick={(selectedItem: any) => {
              setWizardState({
                ...wizardState,
                selLocation: selectedItem,
              });
            }}
            placeholder={t('charger_choose_new_location')}
            placeholderLabelType={
              wizardState.selCompany ? LabelType.BODY3_G4 : LabelType.BODY3_DISABLED
            }
            labelType={LabelType.BODY3}
            labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
            contentDivWidth={220}
            contentDivHeight={200}
            chevdownIconHighlightColor={ColorType.GREY6}
            headerHighLightClassName='bg-grey1 border-grey3 rounded h-[40px] mr-1.5'
            placement='top-end'
            disabled={!locationDropdown.length || !wizardState.selCompany}
          />
        </div>
      </div>
      {auth.role === USER_ROLE.SUPPORT && chargerInfo?.licenseKey && renderDivForMoveLicense()}
      <div className='flex flex-col mb-5'>
        <Button
          label={t('next')}
          type={ButtonType.PRIMARY}
          disabled={
            !(
              wizardState.selCompany &&
              wizardState.selLocation &&
              wizardState.pricingModels.length > 0 &&
              (auth.role !== USER_ROLE.SUPPORT ||
                !chargerInfo?.licenseKey ||
                wizardState.selMoveLicenseOption)
            )
          }
          size={ButtonSize.SMALL_FULL}
          onClick={() => {
            nextStep();
          }}
        />
      </div>
    </div>
  );
};
